import { useEffect, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { CloudUpload as UploadIcon } from '@mui/icons-material';

import PageContainer from '../../../components/layout/PageContainer';
import {
  FBoxContainer,
  FHButton,
  FHInputField,
  FHPageLink,
  FProgressBar,
} from '../../../components/base';
import { useBulkUnbillable } from './hooks';
import { BULK_UNBILLABLE_PASSWORD } from './constants';
import { getBulkUnbillablePasswordInfoText } from './utils';

function BulkUnbillableForm() {
  const {
    loading: claimsLoading,
    summary: bulkUnbillableSummary,
    clearSummary,
    upload,
  } = useBulkUnbillable();

  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState<string>('');

  // Redux state survives navigation, so clear any prior summary on unmount to avoid
  // rendering a stale "Result" block when the page is reused in a session (re-upload
  // is already covered by the thunk's pending reducer).
  useEffect(() => clearSummary, [clearSummary]);

  const isPasswordVerified = password === BULK_UNBILLABLE_PASSWORD;

  const handleSubmit = () => {
    // The Upload button is disabled until both are valid, so this is just narrowing.
    if (!file || !isPasswordVerified) return;
    upload(file);
  };

  const handleDownloadFailures = () => {
    if (!bulkUnbillableSummary?.failures_csv) {
      return;
    }
    const blob = new Blob([bulkUnbillableSummary.failures_csv], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'bulk-unbillable-failures.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PageContainer>
      {claimsLoading && <FProgressBar />}
      <Box sx={{ mt: 3 }}>
        <FHPageLink to="/admin" label="Admin Home" />
        <FBoxContainer
          dataTestId="bulkUnbillableForm"
          sx={{ borderRadius: 2, width: '100%', boxShadow: 'none' }}
        >
          <Grid container>
            <Grid item xs={12} md={12} lg={8}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box ml={1} sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                  <Typography textAlign={'left'} variant="h4">
                    Bulk Mark Notes Unbillable
                  </Typography>
                </Box>
                <Box
                  sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', mt: 1, mb: 2 }}
                />
                <Grid container p={0.5} spacing={1} alignItems="center">
                  <Grid item xs={2.5}>
                    <Typography textAlign={'left'}>Password:</Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <FHInputField
                      inputProps={{
                        'data-testid': 'bulkUnbillablePasswordInput',
                      }}
                      type="password"
                      autoComplete="off"
                      inputHeight={40}
                      sx={{ boxShadow: 'none' }}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      error={!!password && !isPasswordVerified}
                      infoText={getBulkUnbillablePasswordInfoText(password)}
                    />
                  </Grid>
                </Grid>

                {/* The feature itself is hidden until the password is verified, so
                    the page reveals nothing (instructions, uploader, results) to a
                    user who lands here without the password. */}
                {isPasswordVerified && (
                  <>
                    <Typography textAlign={'left'} sx={{ mt: 2, mb: 2 }}>
                      Upload a CSV with columns <strong>Service Code</strong> and{' '}
                      <strong>Note ID</strong> to create Unbillable claims for those notes.
                    </Typography>

                    <Grid container p={0.5} spacing={1} alignItems="center">
                      <Grid item xs={2.5}>
                        <Typography textAlign={'left'}>CSV file:</Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <input
                          data-testid="bulkUnbillableFileInput"
                          type="file"
                          accept=".csv,text/csv"
                          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                        />
                      </Grid>
                    </Grid>

                    <FHButton
                      data-testid="bulkUnbillableSubmitButton"
                      variant="contained"
                      sx={{ mt: 2, width: '200px' }}
                      startIcon={<UploadIcon />}
                      onClick={handleSubmit}
                      disabled={!file || claimsLoading}
                    >
                      Upload CSV
                    </FHButton>

                    {bulkUnbillableSummary && (
                      <Box sx={{ mt: 3 }} data-testid="bulkUnbillableSummary">
                        <Typography variant="h6" textAlign={'left'}>
                          Result
                        </Typography>
                        <Typography textAlign={'left'}>
                          Claims created: {bulkUnbillableSummary.claims_created}
                        </Typography>
                        <Typography textAlign={'left'}>
                          Procedures attached: {bulkUnbillableSummary.procedures_attached}
                        </Typography>
                        <Typography textAlign={'left'}>
                          Rows skipped: {bulkUnbillableSummary.rows_skipped}
                        </Typography>
                        <Typography textAlign={'left'}>
                          Rows failed: {bulkUnbillableSummary.rows_failed}
                        </Typography>
                        {!!bulkUnbillableSummary.failures_csv?.trim() && (
                          <FHButton
                            data-testid="bulkUnbillableDownloadFailures"
                            variant="outlined"
                            sx={{ mt: 1, width: '240px' }}
                            onClick={handleDownloadFailures}
                          >
                            Download failed rows (CSV)
                          </FHButton>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        </FBoxContainer>
      </Box>
    </PageContainer>
  );
}

export default BulkUnbillableForm;
